<template>
  <div id="vuecity" ref="vuecityRoot">
    <div class="pattern-box">
      <span class="mapBack" @click="mapBack(null)">
        <span class="back-icon"></span>
      </span>
      <search
        :can-search-spell="canSearchSpell"
        :clear-text="clearSearch"
        @txtdata="searchText"
      />
    </div>
    <div class="city-body">
      <transition name="list">
        <search-list
          v-if="associationShow"
          :search-list-content="searchListContent"
          @changeName="changeCity"
        />
      </transition>
      <position-box
        :location="location"
        @changeCity="changeCity"
        @locationError="handleLocationError"
      />
      <div class="city-scroll-shell">
        <scroll
          ref="suggest"
          :data="citylist"
          :probe-type="3"
          :listen-scroll="true"
          @distance="distance"
          @scrollStore="scrollStore"
        >
          <div>
            <div ref="scrollTop">
              <hot-city-list
                :hot-cities="hotCities"
                @changeCity="changeCity"
              />
            </div>
            <div v-if="cityLoading" class="city-state">城市列表加载中...</div>
            <div v-else-if="cityError" class="city-state error">{{ cityError }}</div>
            <city-list
              v-else
              :citylist="citylist"
              :element-index="elementIndex"
              @positionCity="changeCity"
              @singleLetter="singleLetter"
            />
          </div>
        </scroll>
        <nav-list
          v-show="!associationShow && citylist.length > 0"
          :nav-list="cityIndexList"
          :flag-text="flagText"
          @toElement="toElement"
        />
        <transition name="flag">
          <div v-if="flag" class="nowFlag">{{ flagText }}</div>
        </transition>
      </div>
    </div>
    <mask-box
      v-if="maskShow"
      :message="maskMessage"
      @chooseing="chooseResult"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import Search from './components/Search.vue';
import Scroll from './base/Scroll.vue';
import PositionBox from './components/PositionBox.vue';
import HotCityList from './components/HotCityList.vue';
import CityList from './components/CityList.vue';
import NavList from './components/NavList.vue';
import MaskBox from './components/MaskBox.vue';
import SearchList from './components/SearchList.vue';
import { getSearchList } from './common/js/search';
import { getDistance } from './common/js/dom';
import { loadOpenCityList } from './common/js/cityData';

const HOT_CITY_NAMES = ['上海', '北京', '广州', '南京', '杭州', '深圳'];

defineOptions({name: 'VueCity'});

const props = defineProps({
  canSearchSpell: {
    type: Boolean,
    default: true
  },
  location: {
    type: Object,
    default: () => ({
      acronym: 'bj',
      firstChar: 'B',
      id: 1,
      name: '北京',
      pinyin: 'beijing',
      rank: 'S'
    })
  },
  hide: {
    type: Function,
    default: null
  },
  backStyle: {
    type: String,
    default: 'retreat'
  }
});

const emit = defineEmits(['closeChooseCity', 'locationError']);
const vuecityRoot = ref(null);
const suggest = ref(null);
const scrollTop = ref(null);
const chooseCityName = ref(null);
const citylist = ref([]);
const cityIndexList = ref(['顶']);
const cityLoading = ref(false);
const cityError = ref('');
const maskShow = ref(false);
const maskMessage = ref({name: '', id: ''});
const associationShow = ref(false);
const searchListContent = ref([]);
const clearSearch = ref(false);
const elementIndex = ref('');
const navTargetText = ref('');
const arrHeight = ref([]);
const flag = ref(false);
const flagText = ref('顶');
let navTargetTimer = null;
let disposed = false;

const flatCityList = computed(() => (
  citylist.value.reduce((cities, group) => cities.concat(group[1]), [])
));

const hotCities = computed(() => (
  HOT_CITY_NAMES
    .map(name => flatCityList.value.find(city => city.name === name))
    .filter(Boolean)
));

onBeforeUnmount(() => {
  disposed = true;
  clearNavTargetTimer();
});

loadCityList();

function clearNavTargetTimer () {
  if (navTargetTimer) {
    clearTimeout(navTargetTimer);
    navTargetTimer = null;
  }
}

async function loadCityList () {
  cityLoading.value = true;
  cityError.value = '';
  try {
    const loadedCityList = await loadOpenCityList();
    if (disposed) {
      return;
    }
    citylist.value = loadedCityList.filter(item => item[1].length > 0);
    cityIndexList.value = ['顶'].concat(citylist.value.map(item => item[0]));
    cityLoading.value = false;
    getDomHeight();
  } catch (error) {
    if (disposed) {
      return;
    }
    console.error('Error loading city list:', error);
    cityError.value = '城市列表加载失败，请稍后重试';
    cityLoading.value = false;
  }
}

function searchText (text) {
  if (!text.length) {
    associationShow.value = false;
    return;
  }
  clearSearch.value = false;
  associationShow.value = true;
  searchListContent.value = getSearchList(text, citylist.value, props.canSearchSpell);
}

function changeCity (city) {
  if (!city) {
    return;
  }
  if (chooseCityName.value && chooseCityName.value.name === city.name) {
    associationShow.value = false;
    clearSearch.value = true;
    return;
  }
  chooseResult(city);
}

function maskClose () {
  maskShow.value = false;
}

function mapBack (result) {
  if (typeof props.hide === 'function') {
    props.hide();
  }
  emit('closeChooseCity', result);
}

function chooseResult (result) {
  if (!result) {
    return;
  }
  chooseCityName.value = result;
  associationShow.value = false;
  clearSearch.value = true;
  if (suggest.value) {
    suggest.value.scrollTo(0, 0, 200);
  }
  maskClose();
  mapBack(result);
}

function handleLocationError (message) {
  emit('locationError', message);
}

function toElement (text) {
  navTargetText.value = text;
  flagText.value = text;
  clearNavTargetTimer();
  navTargetTimer = setTimeout(() => {
    navTargetText.value = '';
    navTargetTimer = null;
  }, 650);

  if (text === '顶') {
    if (suggest.value && scrollTop.value) {
      suggest.value.scrollToElement(scrollTop.value, 300, false, 0);
    }
    elementIndex.value = '';
    return;
  }

  elementIndex.value = '';
  nextTick(() => {
    elementIndex.value = text;
  });
}

function singleLetter (element) {
  if (suggest.value && element) {
    suggest.value.scrollToElement(element, 300, false, 0);
  }
}

function distance (value) {
  if (navTargetText.value) {
    flagText.value = navTargetText.value;
    return;
  }

  elementIndex.value = '';
  if (!arrHeight.value.length) {
    flagText.value = '顶';
    return;
  }
  let index = arrHeight.value.findIndex(item => value < item);
  index = index === -1 ? arrHeight.value.length - 1 : index;
  flagText.value = cityIndexList.value[index] || '顶';
}

function getDomHeight () {
  nextTick(() => {
    const root = vuecityRoot.value;
    const titleElement = root && root.querySelector('.lists .city-title');
    const itemElement = root && root.querySelector('.lists .city-item');
    if (!titleElement || !itemElement || !scrollTop.value) {
      return;
    }

    const heights = getDistance(
      citylist.value,
      titleElement.offsetHeight,
      itemElement.offsetHeight
    );
    heights.unshift(scrollTop.value.offsetHeight);
    let total = 0;
    arrHeight.value = heights.map(value => {
      total += value;
      return total;
    });
    if (suggest.value) {
      suggest.value.refresh();
    }
  });
}

function scrollStore (scrolling) {
  flag.value = scrolling;
  if (!scrolling && suggest.value) {
    const position = suggest.value.getPosition();
    distance(Math.abs(position.y));
  }
}

defineExpose({toElement, singleLetter, distance, getDomHeight});
</script>

<style lang="stylus">
p
  text-align start
#vuecity
  font-family 'Avenir', Helvetica, Arial, sans-serif
  -webkit-font-smoothing antialiased
  -moz-osx-font-smoothing grayscale
  color var(--text-normal)
  height 100%
  width 100%
  font-size 14px
  position absolute
  top 0px
  left 0px
  z-index 1000000000000
  background var(--page-bg)
  display flex
  flex-direction column
  .pattern-box
    width 100%
    height 45px
    flex 0 0 45px
    position relative
    background var(--surface-bg)
    .mapBack
      display block
      width 30px
      height 45px
      line-height 45px
      margin-left 8px
      position absolute
      z-index 2
      .back-icon
        display inline-block
        width 12px
        height 12px
        border-left 2px solid var(--text-main)
        border-bottom 2px solid var(--text-main)
        transform rotate(45deg)
        margin-left 8px
  .city-body
    width 100%
    flex 1 1 auto
    min-height 0
    position relative
    display flex
    flex-direction column
    > .position-box
      flex 0 0 auto
    .city-scroll-shell
      width 100%
      flex 1 1 auto
      min-height 0
      position relative
  .nowFlag
    width 50px
    height 50px
    background var(--selection-color)
    color var(--on-accent-color)
    font-size 30px
    font-weight 900
    line-height 50px
    text-align center
    position absolute
    top 60px
    left 50%
    margin-left -25px
  .city-state
    height 120px
    line-height 120px
    text-align center
    color var(--text-muted)
    background var(--surface-bg)
  .city-state.error
    color var(--accent-color)
.list-enter-active, .list-leave-active
  transition all 0.5s
.list-enter-from
  opacity 0
.list-leave-to
  transform scale(0, 0)
  opacity 0
.flag-leave-active
  transition all 1s
.flag-leave-to
  opacity 0
</style>
